import { useMutation } from "@tanstack/react-query";
import { useState, type FormEvent } from "react";
import { Link, useLocation } from "react-router";
import { WifiOff } from "lucide-react";
import { sessionApi } from "../../api/account";
import { ApiError } from "../../api/client";
import { AuthHeading, AuthLayout, Notice, Repeated, TextButton } from "../../components/account/AuthLayout";
import { EmailField } from "../../components/account/EmailField";
import { Button, PhoneField } from "../../components/ui";
import { usePageTitle } from "../catalogue/usePageTitle";
import { CodeSteps } from "./CodeSteps";
import { destinationFor, destinationProblem, type Channel, type Prefill } from "./RegisterRoute";
import { fieldError, useReturnTo } from "./session";
import { useStore } from "./social";

/**
 * PRD screens 15 and 16: a code to the account's phone or email, then a new password, on
 * the same steps as registering. The answer is the same whether or not there's an account.
 */
export function ForgotPasswordRoute() {
  const returnTo = useReturnTo();
  const prefill: Prefill = (useLocation().state as { prefill?: Prefill } | null)?.prefill ?? {};
  const store = useStore();
  const phoneOffered = store.data ? store.data.sign_in.phone : true;
  const [chosen, setChosen] = useState<Channel>(prefill.channel ?? "phone");
  const channel: Channel = phoneOffered ? chosen : "email";
  const [phone, setPhone] = useState(prefill.phone ?? "");
  const [email, setEmail] = useState(prefill.email ?? "");
  const [problem, setProblem] = useState<string>();
  const [sent, setSent] = useState(false);
  const { destination, shown } = destinationFor(channel, phone, email);
  usePageTitle("Reset your password");

  const send = useMutation({
    mutationFn: () => sessionApi.sendResetCode(destination),
    onSuccess: () => setSent(true),
    onError: (error) => {
      if (error instanceof ApiError && error.code === "phone_unavailable") setChosen("email");
    },
  });

  if (sent) {
    return (
      <AuthLayout back={returnTo}>
        <CodeSteps
          purpose="reset"
          destination={destination}
          shown={shown}
          resendAfter={60}
          onChangeDestination={() => {
            send.reset();
            setSent(false);
          }}
          returnTo={returnTo}
          cartCount={0}
        />
      </AuthLayout>
    );
  }

  const submit = (event: FormEvent) => {
    event.preventDefault();
    const next = destinationProblem(channel, phone, email);
    setProblem(next);
    if (!next) send.mutate();
  };

  const serverField = fieldError(send.error, channel === "phone" ? "phone_number" : "email_address");
  const sendFailed = send.isError && !serverField && !(send.error instanceof ApiError && send.error.code === "phone_unavailable");

  return (
    <AuthLayout back={returnTo}>
      <AuthHeading title="Reset your password">
        {channel === "email" ? "We'll email you a code, then you can choose a new password." : "We'll text you a code, then you can choose a new password."}
      </AuthHeading>
      <form onSubmit={submit} noValidate>
        {channel === "phone" ? (
          <PhoneField
            value={phone}
            error={problem ?? serverField}
            disabled={send.isPending}
            onChange={(digits) => {
              setPhone(digits);
              setProblem(undefined);
            }}
            autoFocus
          />
        ) : (
          <EmailField
            value={email}
            error={problem ?? serverField}
            disabled={send.isPending}
            onChange={(value) => {
              setEmail(value);
              setProblem(undefined);
            }}
            autoFocus
          />
        )}
        {sendFailed && (
          <Notice tone="danger" icon={WifiOff}>
            We couldn't send the code to <Repeated>{shown}</Repeated>. Check your connection, then try again.
          </Notice>
        )}
        <Button type="submit" fullWidth size="lg" disabled={send.isPending} className="mt-6">
          {send.isPending ? "Sending your code…" : sendFailed ? "Try again" : "Send a code"}
        </Button>
      </form>
      {phoneOffered && (
        <div className="mt-5">
          <TextButton
            onClick={() => {
              setChosen(channel === "phone" ? "email" : "phone");
              setProblem(undefined);
              send.reset();
            }}
          >
            {channel === "phone" ? "Use my email instead" : "Use my mobile number instead"}
          </TextButton>
        </div>
      )}
      <p className="mt-7 mb-0 type-small text-text-muted">
        Remembered it?{" "}
        <Link to="/sign-in" state={{ from: returnTo }} className="font-semibold">
          Back to sign in
        </Link>
      </p>
    </AuthLayout>
  );
}
